import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import DepartureSeats from 'components/purchase/DepartureSeats';
import ReturnSeats from 'components/purchase/ReturnSeats';

/**
 * SeatSelectorFactory component for displaying bus diagram.
 * @param {Object} props - Props.
 * @param {String} props.way - Departure or return.
 * @param {Object} props.trip - Object trip
 * @param {function} props.finishSeatsSelection - function to select trip.
 * @param {Object} props.fromTrip - object departure trip only use for return
 * @param {boolean} props.isPurchaseView - boolean to know if is purchase view.
 * @param {function} props.onCloseModal - function to close modal.
 * @param {boolean} props.resultsLayout - boolean to know if is modal mode.
 * @param {Object} props.fromExchange - Exchange data for seat selection limits.
 * @param {boolean} props.isRoundTrip - Flag indicating if the trip is round trip.
 * @param {Function} props.onQuickPurchaseFromSeats - Quick purchase handler from seat selection.
 * @param {number} props.frequentPassengersCount - Number of frequent passengers available.
 * @param {Function} props.onFinishSeatSelection - Override run on finish instead of the default
 *   navigation/finish (used to edit seats inline, e.g. inside the Quick Checkout modal).
 * @param {boolean} props.embedded - Render the selector inline (no nested modal on mobile), to
 *   swap it in place of another view within the same modal.
 * @param {string} props.seatEditMode - Optional edit context for embedded variants.
 * @param {number} props.requiredSeatsCount - Required selected passenger seats for edit context.
 * @returns {JSX.Element} JSX.Element
 */
const SeatSelectorFactory = ({
  way = 'departure',
  trip,
  finishSeatsSelection,
  fromTrip,
  isPurchaseView,
  onCloseModal,
  resultsLayout,
  isRoundTrip,
  fromExchange,
  onQuickPurchaseFromSeats,
  frequentPassengersCount,
  onFinishSeatSelection,
  embedded,
  seatEditMode,
  requiredSeatsCount,
}) => {
  const history = useHistory();

  return way === 'departure' ? (
    <DepartureSeats
      way="departure"
      trip={trip}
      history={history}
      isPurchaseView={isPurchaseView}
      finishSeatsSelection={finishSeatsSelection}
      onClose={onCloseModal}
      resultsLayout={resultsLayout}
      isRoundTrip={isRoundTrip}
      fromExchange={fromExchange}
      onQuickPurchaseFromSeats={onQuickPurchaseFromSeats}
      frequentPassengersCount={frequentPassengersCount}
      onFinishSeatSelection={onFinishSeatSelection}
      embedded={embedded}
      seatEditMode={seatEditMode}
      requiredSeatsCount={requiredSeatsCount}
    />
  ) : (
    <ReturnSeats
      way="return"
      trip={trip}
      history={history}
      isPurchaseView={isPurchaseView}
      finishSeatsSelection={finishSeatsSelection}
      fromTrip={fromTrip}
      onClose={onCloseModal}
      fromExchange={fromExchange}
      resultsLayout={resultsLayout}
      isRoundTrip={isRoundTrip}
      onQuickPurchaseFromSeats={onQuickPurchaseFromSeats}
      frequentPassengersCount={frequentPassengersCount}
      onFinishSeatSelection={onFinishSeatSelection}
      embedded={embedded}
      seatEditMode={seatEditMode}
      requiredSeatsCount={requiredSeatsCount}
    />
  );
};

SeatSelectorFactory.propTypes = {
  trip: PropTypes.object,
  way: PropTypes.string.isRequired,
  finishSeatsSelection: PropTypes.func,
  fromTrip: PropTypes.object,
  isPurchaseView: PropTypes.bool,
  onCloseModal: PropTypes.func,
  resultsLayout: PropTypes.bool,
  isRoundTrip: PropTypes.bool,
  fromExchange: PropTypes.object,
  onQuickPurchaseFromSeats: PropTypes.func,
  frequentPassengersCount: PropTypes.number,
  onFinishSeatSelection: PropTypes.func,
  embedded: PropTypes.bool,
  seatEditMode: PropTypes.oneOf(['quickCheckout']),
  requiredSeatsCount: PropTypes.number,
};

export default SeatSelectorFactory;
